import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Shield, Sparkles, Moon, Star, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/ProductCard";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/products?featured=true`);
        setFeaturedProducts(response.data.slice(0, 4));
      } catch (error) {
        console.error("Error fetching featured products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  const features = [
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Pan India Shipping",
      description: "Delivering cosmic energy across all pincodes"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Authentic Crystals",
      description: "100% genuine, ethically sourced from the earth"
    },
    {
      icon: <Moon className="w-6 h-6" />,
      title: "Moon Charged",
      description: "Every crystal charged under the full moon"
    },
    {
      icon: <Wand2 className="w-6 h-6" />,
      title: "Energy Cleansed",
      description: "Purified with sacred rituals before shipping"
    }
  ];

  return (
    <div className="bg-mystic-void" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1710270822096-ccfa274be004?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxuZWJ1bGElMjBzdGFycyUyMGNvc21pYyUyMGJhY2tncm91bmQlMjBkYXJrJTIwcHVycGxlJTIwYmx1ZXxlbnwwfHx8fDE3NjgyMTgzNzN8MA&ixlib=rb-4.1.0&q=85"
            alt="Cosmic background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
          <div className="absolute inset-0 mystic-overlay" />
        </div>

        {/* Stars overlay */}
        <div className="absolute inset-0 stars-bg opacity-50" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-4 h-4 text-mystic-gold animate-twinkle" />
              <p className="text-mystic-gold font-medium tracking-widest uppercase text-sm">
                Unlock Your Spiritual Power
              </p>
              <Star className="w-4 h-4 text-mystic-gold animate-twinkle" style={{ animationDelay: '0.5s' }} />
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-mystic-silver leading-tight mb-6 text-glow">
              Discover the <br />
              <span className="font-semibold italic text-mystic-orchid">Mystical</span> Realm
            </h1>
            <p className="text-mystic-mist text-lg mb-8 max-w-lg">
              Ancient crystals holding cosmic energy, carefully curated and delivered 
              to spiritual seekers across India.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/products" data-testid="hero-shop-btn">
                <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full px-8 py-6 text-base font-serif italic btn-lift neon-glow hover:shadow-[0_0_30px_rgba(168,85,247,0.6)]">
                  Explore Collection
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="hero-contact-btn">
                <Button 
                  variant="outline" 
                  className="rounded-full px-8 py-6 text-base border-mystic-amethyst/50 text-mystic-amethyst hover:bg-mystic-amethyst/20 hover:border-mystic-amethyst"
                >
                  Seek Guidance
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Floating crystal decoration */}
        <motion.div 
          className="absolute right-10 top-1/3 hidden lg:block"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        >
          <Sparkles className="w-16 h-16 text-mystic-amethyst/30" />
        </motion.div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 nebula-bg">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-mystic-amethyst font-medium tracking-widest uppercase text-sm mb-3">
              ✧ Sacred Collections ✧
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-mystic-silver italic">
              Choose Your Path
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Healing Crystals */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Link 
                to="/products?category=healing" 
                className="block category-card group"
                data-testid="category-healing"
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1747885420344-de3b92f8c6a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxteXN0aWMlMjBjcnlzdGFsJTIwYW1ldGh5c3QlMjBxdWFydHolMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NjgyMTgzNzB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Healing Crystals"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-mystic-void via-mystic-void/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <h3 className="font-serif text-3xl text-mystic-silver mb-2 italic text-glow">✧ Healing Crystals</h3>
                    <p className="text-mystic-mist/80 text-sm mb-4">
                      Polished & charged for energy work
                    </p>
                    <span className="inline-flex items-center text-mystic-gold font-medium group-hover:text-mystic-orchid transition-colors">
                      Enter Portal <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Raw Stones */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Link 
                to="/products?category=raw" 
                className="block category-card group"
                data-testid="category-raw"
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                  <img
                    src="https://images.unsplash.com/photo-1545128309-686a71a99f76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxteXN0aWMlMjBjcnlzdGFsJTIwYW1ldGh5c3QlMjBxdWFydHolMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NjgyMTgzNzB8MA&ixlib=rb-4.1.0&q=85"
                    alt="Raw Stones"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-mystic-void via-mystic-void/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <h3 className="font-serif text-3xl text-mystic-silver mb-2 italic text-glow">◆ Raw Stones</h3>
                    <p className="text-mystic-mist/80 text-sm mb-4">
                      Unpolished natural specimens
                    </p>
                    <span className="inline-flex items-center text-mystic-gold font-medium group-hover:text-mystic-orchid transition-colors">
                      Enter Portal <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12"
          >
            <div>
              <p className="text-mystic-amethyst font-medium tracking-widest uppercase text-sm mb-3">
                ✨ Handpicked for You ✨
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light text-mystic-silver italic">
                Featured Crystals
              </h2>
            </div>
            <Link to="/products" data-testid="view-all-products">
              <Button 
                variant="outline" 
                className="rounded-full border-mystic-amethyst/50 text-mystic-amethyst hover:bg-mystic-amethyst/20 hover:border-mystic-amethyst"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square bg-white/5 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="featured-products-grid">
              {featuredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-mystic-purple/30 to-mystic-void">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center glass rounded-2xl p-6 neon-glow-hover"
                data-testid={`feature-${index}`}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-mystic-amethyst/20 flex items-center justify-center text-mystic-gold">
                  {feature.icon}
                </div>
                <h3 className="font-serif text-xl text-mystic-silver mb-2 italic">{feature.title}</h3>
                <p className="text-mystic-mist/70 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-mystic-gold font-medium tracking-widest uppercase text-sm mb-3">
                ◇ Our Story ◇
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl font-light text-mystic-silver mb-6 italic text-glow">
                Connecting You with <br />
                <span className="text-mystic-orchid">Cosmic Energy</span>
              </h2>
              <div className="space-y-4 text-mystic-mist">
                <p>
                  At Crystal Haven, we are guardians of ancient wisdom, bridging the gap 
                  between the mystical realm and seekers of spiritual enlightenment.
                </p>
                <p>
                  Each crystal in our collection is hand-selected during auspicious lunar phases, 
                  cleansed through sacred rituals, and charged with intention before finding 
                  its way to you.
                </p>
                <p>
                  Whether you're awakening your third eye or seeking protection, 
                  we guide you in discovering the perfect stone for your spiritual journey.
                </p>
              </div>
              <Link to="/contact" className="inline-block mt-8" data-testid="about-contact-btn">
                <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full px-8 py-6 text-base font-serif italic btn-lift neon-glow">
                  Begin Your Journey
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 neon-glow">
                <img
                  src="https://images.unsplash.com/photo-1747576129956-d8124fab8668?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxzcGlyaXR1YWwlMjBzeW1ib2xzJTIwc2FjcmVkJTIwZ2VvbWV0cnklMjBnb2xkJTIwb24lMjBibGFja3xlbnwwfHx8fDE3NjgyMTgzNzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Spiritual symbols"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-2xl shadow-xl neon-glow">
                <p className="font-serif text-4xl font-semibold italic">500+</p>
                <p className="text-sm opacity-90">Seekers Awakened</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-mystic-void to-mystic-purple/20 stars-bg">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl sm:text-5xl font-light text-mystic-silver mb-6 italic text-glow">
              Ready to Awaken Your <br />
              <span className="text-mystic-orchid">Inner Power?</span>
            </h2>
            <p className="text-mystic-mist text-lg mb-8 max-w-2xl mx-auto">
              Browse our sacred collection of healing crystals and raw stones. 
              Order via WhatsApp for personalized spiritual guidance.
            </p>
            <Link to="/products" data-testid="cta-shop-btn">
              <Button className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full px-10 py-6 text-lg font-serif italic btn-lift neon-glow animate-glow-pulse">
                Enter the Realm
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
